import React, { useCallback, useEffect, useState } from "react";

import ShipeeziGrid, { Column } from "Components/AppGrid/shipeezi-new-grid";
import _ from "lodash";
import { useTranslation } from "react-i18next";
import { RiErrorWarningFill } from "react-icons/ri";

import { CartonProductView, CartonType, CartonView } from "../../../../../models/carton";
import { handleGetConvertValue } from "../../../../../util/converts";
import { DecimalFormatType } from "../../../../../util/formatDecimalValue";
import { getMeasurement, getWeightMeasurement } from "../../../../../util/getMeasurement";
import { DefaultTooltip } from "../../../Tooltip";
import { EditProductModalHandles } from "../ModalEditProduct";
import useStyles from "./styles";
import { formatHSCode } from "../../../../../pages/private/Shared/Settings/Configurations/HSCommodityCode/utils";
import { should } from "chai";

interface Props {
  carton: CartonView;
  cartons: CartonView[];
  updateCartons?: (cartons: CartonView[]) => void;
  editProductRef?: React.RefObject<EditProductModalHandles>;

  isLengthMandatory?: boolean;
  viewOnly?: boolean;
  calledFromBooking?: boolean;
  notEditProduct?: boolean;

  shouldShowUnitField?: boolean;
  showOriginCriterion?: boolean;
}

interface SubColumn extends Column {
  name: keyof Partial<CartonProductView> | "status";
}

const AfterRow = (props: Props) => {
  const {
    carton,
    cartons,
    isLengthMandatory,
    viewOnly,
    calledFromBooking,
    updateCartons,
    editProductRef,
    notEditProduct,
    shouldShowUnitField = false,
    showOriginCriterion,
  } = props;
  const { t: translate } = useTranslation();
  const classes = useStyles();
  const measurement = getMeasurement();
  const measurementWeight = getWeightMeasurement();
  const openEditProductModal = useCallback(
    (sscc: string, productIndex: number, product: CartonProductView, viewOnly?: boolean) =>
      editProductRef?.current?.open(sscc, productIndex, product, viewOnly),
    []
  );

  const [products, setProducts] = useState<CartonProductView[]>([]);

  useEffect(() => {
    setProducts(carton.products ?? []);
  }, [carton.products]);

  if (!carton || !carton.products || !carton.products.length) return null;

  const isProductMissingDetails = (product: CartonProductView) =>
    !product?.construction ||
    !product?.lining ||
    !product?.category ||
    !product?.fabric ||
    !product?.composition ||
    !product?.manufactureDetails ||
    !product?.gender ||
    (isLengthMandatory ? !product?.length : false) ||
    (shouldShowUnitField ? !product?.unitOfMeasure : false) ||
    (showOriginCriterion &&
      (!product?.originCriterion || !["W", "P"].includes(product.originCriterion)));

  const renderMissingDetails = (row: CartonProductView) => {
    if (isProductMissingDetails(row))
      return (
        <div className={classes.printLabelButton}>
          <DefaultTooltip title={translate("someInformationOfThisSkuAreMissing")}>
            <div className={classes.missingDetailIcon}>
              <RiErrorWarningFill color="#FEC84B" />
            </div>
          </DefaultTooltip>
          <span>{row.sku}</span>
        </div>
      );

    return <span>{row.sku}</span>;
  };

  const onEditProductFromCarton = (sscc?: string, productIndex?: number | string) => {
    if (!sscc || !productIndex || Number.isNaN(Number(productIndex)) || !updateCartons) return;

    const carton = cartons.find((carton) => carton.sscc === sscc);

    if (!carton) return;

    if (!products || products.length === 0) return;

    openEditProductModal(sscc, Number(productIndex), products[Number(productIndex)]);
  };

  const onViewProductFromCarton = (sscc?: string, productIndex?: number | string) => {
    if (!sscc || !productIndex || Number.isNaN(Number(productIndex))) return;

    const carton = cartons.find((carton) => carton.sscc === sscc);

    if (!carton) return;

    if (!products || products.length === 0) return;

    openEditProductModal(sscc, Number(productIndex), products[Number(productIndex)], true);
  };

  const onDeleteProductFromCarton = (sscc?: string, productIndex?: string | number) => {
    if (!sscc || Number.isNaN(Number(productIndex)) || !updateCartons || notEditProduct) return;
    const cloned = _.cloneDeep(cartons);

    const cartonIndex = cloned.findIndex((carton) => carton.sscc === sscc);

    if (cartonIndex < -1) return;

    const carton = cloned[cartonIndex];

    if (!products || products.length === 0) return;

    const removedProduct = products[Number(productIndex)];

    if (removedProduct) {
      const removeProductFromCartonProduct = carton.cartonProducts?.filter(
        (cp) => cp.productId !== removedProduct.id
      );
      carton.cartonProducts = removeProductFromCartonProduct;
    }

    products.splice(Number(productIndex), 1);

    const cartonType = products.length > 1 ? CartonType.MIXED : CartonType.SOLID;

    carton.type = cartonType;
    carton.products = products;

    cloned[cartonIndex] = carton;

    updateCartons(cloned);
  };

  const subColumns: SubColumn[] = [
    {
      name: "sku",
      title: `${translate("sku")} #`,
      hideSort: true,
      className: classes.sku,
      formatComponent: (_: string, row: CartonProductView) => renderMissingDetails(row),
    },
    {
      name: "poNumber",
      title: `${translate("order")} #`,
      hideSort: true,
      className: classes.poNumber,
    },
    {
      name: "description",
      title: translate("productName"),
      hideSort: true,
      className: classes.description,
    },
    {
      name: "colorDescription",
      title: translate("colour"),
      hideSort: true,
      className: classes.color,
    },
    {
      name: "size",
      title: translate("size"),
      hideSort: true,
      className: classes.size,
    },
    ...(showOriginCriterion
      ? [
          {
            name: "originCriterion" as keyof Partial<CartonProductView>,
            title: translate("originCriterion"),
            hideSort: true,
            className: classes.originCriterion,
          },
        ]
      : []),
    {
      name: "hsCode",
      title: translate("hsCommodityCode"),
      hideSort: true,
      className: classes.hscode,
      style: {
        paddingRight: "8px",
      },
      formatText: (value: string) => (value ? formatHSCode(value) : ""),
    },
    {
      name: "cfsCbm",
      title: `${translate("volume")} (${(measurement?.code || "")?.toUpperCase()})`,
      hideSort: true,
      className: classes.cbm,
      formatText: (cbm?: string) =>
        handleGetConvertValue(Number(cbm || 0), measurement?.code, DecimalFormatType.MAERSK),
      style: {
        minWidth: "80px",
      },
    },
    {
      name: "cfsWeight",
      title: `${translate("weight")} (${(measurementWeight?.code || "")?.toUpperCase()})`,
      hideSort: true,
      className: classes.weight,
      formatText: (weight?: string) =>
        handleGetConvertValue(
          Number(weight || 0),
          measurementWeight?.code,
          DecimalFormatType.MAERSK
        ),
      style: {
        minWidth: "80px",
      },
    },
    {
      name: "quantityInCarton",
      title: translate("quantity"),
      hideSort: true,
      className: classes.quantity,
    },
    ...(shouldShowUnitField
      ? [
          {
            name: "unitOfMeasure",
            title: translate("unitOfMeasure"),
            hideSort: true,
            className: classes.quantity,
            formatText: (unit?: string) => unit?.toUpperCase(),
          } as SubColumn,
        ]
      : []),
  ];

  return (
    <div className={classes.subGrid}>
      <ShipeeziGrid
        data={products || carton.products}
        columns={subColumns}
        keyExtractor={(_, index) => String(index)}
        showHeaderGrid={false}
        showFooterGrid={false}
        showFooterPageInfo={false}
        showSelection={false}
        showSort={false}
        positionActionsColumn="right"
        rowActions={
          viewOnly || carton?.isClosed || notEditProduct || !calledFromBooking
            ? {
                handleDetails: (rowId) => onViewProductFromCarton(carton.sscc, rowId),
              }
            : {
                handleEdit: (rowId) => onEditProductFromCarton(carton.sscc, rowId),
                handleDelete: (rowId) => onDeleteProductFromCarton(carton.sscc, rowId),
              }
        }
        showColumnChooser={false}
      />
    </div>
  );
};

export default AfterRow;
